/**
 * WalletCards — Apple Wallet-aktige SAS-kort for React (TypeScript)
 * =============================================================================
 * Selvstendig: eneste avhengighet er `react`. Ingen Tailwind, ingen ekstern CSS
 * eller bildefiler kreves – SAS EuroBonus-logoen ligger innebygd, og
 * interaksjons-CSS injiseres automatisk. Slipp fila rett inn i et Vite + React-
 * prosjekt og importer det du trenger:
 *
 *   import { SASEuroBonusMastercard, SASAmexElite } from "./WalletCards";
 *
 *   <SASEuroBonusMastercard
 *     cardNumber="5412 7500 0000 8841"
 *     holderName="OLA NORDMANN"
 *     expiry="12/27"
 *     width={340}
 *     flipAxis="horizontal"
 *   />
 *
 * Alt skaleres ut fra `width`. Kortene er piksel-presise; animasjoner er
 * GPU-drevne (kun transform) og myke, med hover-løft, trykk-respons og 3D-flip.
 * =============================================================================
 */

import type React from "react";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/* ------------------------------------------------------------------ design */

/** Designet er tegnet i et 380×240-rutenett (kredittkort-forhold ~1.586:1). */
const DESIGN = { width: 380, ratio: 1.586 } as const;

/** Farger som er greie å tweake per variant. */
const THEME = {
  mastercard: { front: "#d6d6d4", divider: "#8e8e8e", back: "#d6d6d4", backText: "#2b2b2b" },
  amex: { back: "#141414", backText: "#e7e7e7" },
} as const;

/* ------------------------------------------------------- interaksjons-CSS */

const CARD_STYLE_ID = "wallet-cards-styles";

/* GPU-drevne, myke animasjoner. Flip ligger på .wc-flipper (3D, roterer med
   skyggen så ingen statisk bakgrunn vises). Hover/trykk ligger på .wc-root
   (egen transform – kolliderer ikke med flippen). prefers-reduced-motion
   respekteres. */
const CARD_CSS = `
.wc-root{display:inline-block;cursor:pointer;outline:none;-webkit-tap-highlight-color:transparent;touch-action:manipulation;transition:transform .45s cubic-bezier(.22,1,.36,1);}
.wc-root:hover{transform:translateY(-2px);}
.wc-root:active{transform:translateY(-1px) scale(.995);transition-duration:.22s;}
.wc-flipper{position:relative;transform-style:preserve-3d;-webkit-transform-style:preserve-3d;will-change:transform;transition:transform .5s cubic-bezier(.2,.8,.2,1),box-shadow .5s cubic-bezier(.22,1,.36,1);box-shadow:0 3px 1px -2px rgba(0,0,0,.14),0 2px 2px 0 rgba(0,0,0,.10),0 1px 5px 0 rgba(0,0,0,.08);}
.wc-root:hover .wc-flipper{box-shadow:0 5px 5px -3px rgba(0,0,0,.16),0 8px 10px 1px rgba(0,0,0,.12),0 3px 14px 2px rgba(0,0,0,.08);}
.wc-root:focus-visible .wc-flipper{box-shadow:0 0 0 3px rgba(82,140,255,.8),0 2px 2px 0 rgba(0,0,0,.10),0 1px 5px 0 rgba(0,0,0,.08);}
.wc-face{position:absolute;inset:0;overflow:hidden;backface-visibility:hidden;-webkit-backface-visibility:hidden;}
@media (prefers-reduced-motion:reduce){.wc-root,.wc-flipper{transition:none!important;}.wc-root:hover{transform:none;}.wc-root:hover .wc-flipper{box-shadow:0 3px 1px -2px rgba(0,0,0,.14),0 2px 2px 0 rgba(0,0,0,.10),0 1px 5px 0 rgba(0,0,0,.08);}}
`;

/** Injiserer kort-CSS i <head> én gang (idempotent, SSR-trygt). */
function useCardStyles() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(CARD_STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = CARD_STYLE_ID;
    el.textContent = CARD_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ------------------------------------------------------------- hjelpere */

const onlyDigits = (v = ""): string => String(v).replace(/\D/g, "");
const lastFour = (v = ""): string => onlyDigits(v).slice(-4) || "0000";
const groupFour = (v = ""): string =>
  onlyDigits(v)
    .replace(/(.{4})/g, "$1 ")
    .trim();
/**
 * Format a card number for the back face. When we only know the last 4
 * (typical for aggregator-sourced cards — banks never expose full PAN), pad
 * with dot-groups so the layout still reads as a card number. When we have
 * the full PAN, group it normally.
 */
const formatBackNumber = (v = ""): string => {
  const digits = onlyDigits(v);
  if (digits.length === 0) return "•••• •••• •••• ••••";
  if (digits.length <= 4) return `•••• •••• •••• ${digits.padStart(4, "•")}`;
  return groupFour(digits);
};

/** Lager en px-skalerer for en gitt kortbredde (n i design-px → faktiske px). */
const createPx = (width: number) => {
  const s = width / DESIGN.width;
  return (n: number) => n * s;
};

/* --------------------------------------------------------------- typer */

export type FlipAxis = "horizontal" | "vertical";

/** Imperativt API tilgjengelig via ref på kortkomponentene. */
export interface WalletCardHandle {
  /** Snu kortet (toggle for-/bakside). */
  flip: () => void;
  /** Sett flippet tilstand eksplisitt. */
  setFlipped: (flipped: boolean) => void;
  /** Om baksiden vises akkurat nå. */
  flipped: boolean;
}

/** Felles props for begge kortvariantene. */
export interface BaseCardProps {
  /** Fullt kortnummer. Kun siste 4 vises på forsiden; fullt nummer på baksiden. */
  cardNumber?: string;
  /** Navn på kortholder (vises i versaler). */
  holderName?: string;
  /** Utløpsdato, format "MM/ÅÅ". */
  expiry?: string;
  /** Kortbredde i px. Alt skaleres proporsjonalt ut fra denne. */
  width?: number;
  /** Hvilken akse kortet snur om. */
  flipAxis?: FlipAxis;
  /** Styrt flippet tilstand. Utelat for ukontrollert (klikk for å snu). */
  flipped?: boolean;
  /** Startverdi for ukontrollert flip. */
  defaultFlipped?: boolean;
  /** Kalles når flippet tilstand endres. */
  onFlip?: (flipped: boolean) => void;
  /** Overstyr SAS EuroBonus-logoen, f.eks. "/sas_eurobonus.webp". Default: innebygd. */
  logoSrc?: string;
  /** Klasse på ytre element (for plassering/margin i ditt layout). */
  className?: string;
  /** Inline-stil på ytre element. */
  style?: React.CSSProperties;
}

export interface CardProps extends BaseCardProps {}
export interface AmexCardProps extends BaseCardProps {
  /** Tekst under "Member Since" (Amex-kortet). */
  memberSince?: string;
}

/* ----------------------------------------------------------- AutoFitText */

interface AutoFitTextProps {
  text: string;
  maxWidth: number;
  fontSize: number;
  minFontSize?: number;
  style?: React.CSSProperties;
}
/** Krymper teksten på én linje til den passer innenfor maxWidth (ned til minFontSize). */
const AutoFitText = memo(function AutoFitText({
  text,
  maxWidth,
  fontSize,
  minFontSize,
  style,
}: AutoFitTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState(fontSize);
  const floor = minFontSize ?? fontSize * 0.6;

  // biome-ignore lint/correctness/useExhaustiveDependencies: text/maxWidth are intentional triggers to reset before re-measuring
  useLayoutEffect(() => {
    setSize(fontSize);
  }, [text, fontSize, maxWidth]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: text is an intentional trigger to re-measure when content changes
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.scrollWidth;
    if (w > maxWidth && size > floor) {
      setSize(Math.max(floor, size * (maxWidth / w)));
    }
  }, [size, text, maxWidth, floor]);

  return (
    <span
      ref={ref}
      style={{ fontSize: size, whiteSpace: "nowrap", display: "inline-block", ...style }}
    >
      {text}
    </span>
  );
});

/* --------------------------------------------------------------- logoer */

/**
 * Den ekte SAS EuroBonus-logoen som transparent WebP (hvit logo, ingen bakgrunn),
 * innebygd som data-URI så komponenten er selvstendig. Vil du heller bruke en fil,
 * legg `sas_eurobonus.webp` i public/ og send inn `src="/sas_eurobonus.webp"`.
 */
export const SAS_EUROBONUS_LOGO_SRC =
  "data:image/webp;base64,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";
/** Forhold bredde/høyde for logoen. */
export const SAS_EUROBONUS_RATIO = 1.5423;

export interface SASEuroBonusLogoProps {
  /** Høyde i px (bredden følger automatisk). */
  height?: number | undefined;
  /** Bildekilde. Default: innebygd logo. */
  src?: string | undefined;
  alt?: string | undefined;
  style?: React.CSSProperties | undefined;
}
/** Gjenbrukbar SAS EuroBonus-logo (kun bilde, transparent bakgrunn). */
export const SASEuroBonusLogo = memo(function SASEuroBonusLogo({
  height = 50,
  src = SAS_EUROBONUS_LOGO_SRC,
  alt = "SAS EuroBonus",
  style,
}: SASEuroBonusLogoProps) {
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      style={{ height, width: "auto", display: "block", userSelect: "none", ...style }}
    />
  );
});

const MastercardLogo = memo(function MastercardLogo({ size = 58 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.71} viewBox="0 0 152 108" aria-label="Mastercard">
      <circle cx="60" cy="54" r="36" fill="#EB001B" />
      <circle cx="92" cy="54" r="36" fill="#F79E1B" />
      <path d="M76 21.75 A36 36 0 0 1 76 86.25 A36 36 0 0 1 76 21.75 Z" fill="#FF5F00" />
    </svg>
  );
});

const AmexBoxLogo = memo(function AmexBoxLogo({
  size = 50,
  color = "#fff",
  solid = false,
}: {
  size?: number;
  color?: string;
  solid?: boolean;
}) {
  const fs = size * 0.16;
  return (
    <div
      role="img"
      aria-label="American Express"
      style={{
        width: size,
        height: size,
        border: `${Math.max(1.5, size * 0.045)}px solid ${color}`,
        background: solid ? "#006FCF" : "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: size * 0.06,
        padding: `0 ${size * 0.06}px`,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {["AMERICAN", "EXPRESS"].map((t) => (
        <span
          key={t}
          style={{
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            fontWeight: 800,
            fontSize: fs,
            lineHeight: 1,
            letterSpacing: 0,
            whiteSpace: "nowrap",
            color: solid ? "#fff" : color,
            transform: "scaleX(0.85)",
            transformOrigin: "center",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
});

const Contactless = memo(function Contactless({
  size = 22,
  color = "#fff",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Kontaktløs betaling">
      <title>Kontaktløs betaling</title>
      <g fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
        <path d="M7 8a7 7 0 0 1 0 8" />
        <path d="M11 5.5a11 11 0 0 1 0 13" />
        <path d="M15 3a15 15 0 0 1 0 18" />
      </g>
    </svg>
  );
});

/* ------------------------------------------------------------- base-kort */

export interface WalletCardProps {
  width?: number | undefined;
  radius?: number | undefined;
  flipAxis?: FlipAxis | undefined;
  flipped?: boolean | undefined;
  defaultFlipped?: boolean | undefined;
  onFlip?: ((flipped: boolean) => void) | undefined;
  front: React.ReactNode;
  back: React.ReactNode;
  ariaLabel?: string | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

/**
 * Felles base: ramme, skalering, skygge og 3D-flip. Eksportert så du kan bygge
 * egne kortvarianter ved å sende inn `front`/`back`. Støtter ref (WalletCardHandle).
 */
export const WalletCard = forwardRef<WalletCardHandle, WalletCardProps>(function WalletCard(
  {
    width = 360,
    radius,
    flipAxis = "horizontal",
    flipped,
    defaultFlipped = false,
    onFlip,
    front,
    back,
    ariaLabel = "Betalingskort",
    className,
    style,
  },
  ref,
) {
  useCardStyles();
  const [inner, setInner] = useState(defaultFlipped);
  const isControlled = typeof flipped === "boolean";
  const isFlipped = typeof flipped === "boolean" ? flipped : inner;

  // Round to integer pixels so the face/flipper don't sit at sub-pixel offsets
  // — sub-pixel heights cause a hairline gap to appear at the bottom edge when
  // the card is composited with the hover transform.
  const height = Math.round(width / DESIGN.ratio);
  const r = radius ?? width * 0.062;

  const commit = (v: boolean) => {
    if (!isControlled) setInner(v);
    onFlip?.(v);
  };
  const toggle = () => commit(!isFlipped);

  useImperativeHandle(ref, () => {
    const setFlipped = (v: boolean) => {
      if (!isControlled) setInner(v);
      onFlip?.(v);
    };
    return {
      flip: () => setFlipped(!isFlipped),
      setFlipped,
      flipped: isFlipped,
    };
  }, [isFlipped, isControlled, onFlip]);

  const axisTransform = (deg: number) =>
    flipAxis === "vertical" ? `rotateX(${deg}deg)` : `rotateY(${deg}deg)`;

  return (
    <button
      type="button"
      aria-label={`${ariaLabel} – trykk for å snu`}
      aria-pressed={isFlipped}
      onClick={toggle}
      className={className ? `wc-root ${className}` : "wc-root"}
      style={{
        perspective: width * 3,
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: 0,
        font: "inherit",
        color: "inherit",
        ...style,
      }}
    >
      <div
        className="wc-flipper"
        style={{ width, height, borderRadius: r, transform: axisTransform(isFlipped ? 180 : 0) }}
      >
        <div className="wc-face" style={{ borderRadius: r, transform: "translateZ(1px)" }}>
          {front}
        </div>
        <div
          className="wc-face"
          style={{ borderRadius: r, transform: `${axisTransform(180)} translateZ(1px)` }}
        >
          {back}
        </div>
      </div>
    </button>
  );
});

/* --------------------------------------------------------------- bakside */

interface CardBackProps {
  width: number;
  theme: { back: string; backText: string };
  last4Full: string;
  holderName: string;
  brand: React.ReactNode;
}
function CardBack({ width, theme, last4Full, holderName, brand }: CardBackProps) {
  const px = createPx(width);
  return (
    <div style={{ position: "absolute", inset: 0, background: theme.back }}>
      {/* magnetstripe */}
      <div
        style={{
          position: "absolute",
          top: px(26),
          left: 0,
          right: 0,
          height: px(38),
          background: "#0b0b0b",
        }}
      />
      {/* signaturfelt */}
      <div
        style={{
          position: "absolute",
          top: px(82),
          left: px(20),
          right: px(64),
          height: px(34),
          borderRadius: px(4),
          background: "repeating-linear-gradient(45deg,#efefe9 0 8px,#e3e3db 8px 16px)",
          display: "flex",
          alignItems: "center",
          paddingLeft: px(10),
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
          fontStyle: "italic",
          fontSize: px(13),
          color: "#3a3a3a",
          letterSpacing: px(0.5),
        }}
      >
        {holderName || "KORTHOLDER"}
      </div>
      {/* CVV */}
      <div
        style={{
          position: "absolute",
          top: px(82),
          right: px(20),
          height: px(34),
          minWidth: px(40),
          borderRadius: px(4),
          background: "#fff",
          color: "#222",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "monospace",
          fontSize: px(13),
          letterSpacing: px(1),
        }}
      >
        •••
      </div>
      {/* Masked card number on the back. Expiry is deliberately omitted —
          Enable Banking doesn't expose card expiry, so showing a fixed
          "MM/ÅÅ" placeholder would be fake info. */}
      <div
        style={{
          position: "absolute",
          left: px(20),
          top: px(128),
          color: theme.backText,
          fontFamily: "monospace",
          fontSize: px(13),
          letterSpacing: px(1.5),
        }}
      >
        {formatBackNumber(last4Full)}
      </div>
      {/* merke nede til høyre */}
      <div style={{ position: "absolute", right: px(18), bottom: px(16) }}>{brand}</div>
    </div>
  );
}

/* ===================== VARIANT 1: SAS EUROBONUS MASTERCARD ================= */

const SASMastercardFront = memo(function SASMastercardFront({
  width,
  last4,
  logoSrc,
}: {
  width: number;
  last4: string;
  logoSrc?: string | undefined;
}) {
  const px = createPx(width);
  return (
    <div style={{ position: "absolute", inset: 0, background: THEME.mastercard.front }}>
      {/* én ren, jevn skillelinje (sirkelbue): lys grå → mørkere grå */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 380 240"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
        style={{ position: "absolute", inset: 0 }}
      >
        {/* 1-units overdraw på bunn og høyre kant (y=241, x=381) — klippes
            vekk av parent-ens overflow:hidden + border-radius, men hindrer
            en synlig lys stripe ved kortets nedre kant som ellers oppstår
            pga sub-pixel anti-alias på SVG-pathens grense. */}
        <path
          d="M160 0 L381 0 L381 241 L290 241 C 291 143, 242 53, 160 0 Z"
          fill={THEME.mastercard.divider}
        />
      </svg>

      <div style={{ position: "absolute", left: px(28), top: px(24) }}>
        <SASEuroBonusLogo height={px(54)} src={logoSrc} />
      </div>

      <div style={{ position: "absolute", right: px(26), top: px(28) }}>
        <MastercardLogo size={px(58)} />
      </div>

      <div
        style={{
          position: "absolute",
          left: px(30),
          bottom: px(26),
          display: "flex",
          alignItems: "center",
          gap: px(10),
          color: THEME.mastercard.backText,
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
          fontWeight: 500,
          fontSize: px(16),
          letterSpacing: px(0.5),
        }}
      >
        <span
          style={{ fontSize: px(10), letterSpacing: px(2), transform: `translateY(${-px(2)}px)` }}
        >
          ••••
        </span>
        {last4}
      </div>
    </div>
  );
});

/** SAS EuroBonus Mastercard (lys grå). */
export const SASEuroBonusMastercard = forwardRef<WalletCardHandle, CardProps>(
  function SASEuroBonusMastercard(
    {
      cardNumber = "5412 7500 0000 8841",
      holderName = "OLA NORDMANN",
      width = 360,
      flipAxis = "horizontal",
      flipped,
      defaultFlipped,
      onFlip,
      logoSrc,
      className,
      style,
    },
    ref,
  ) {
    return (
      <WalletCard
        ref={ref}
        width={width}
        flipAxis={flipAxis}
        flipped={flipped}
        defaultFlipped={defaultFlipped}
        onFlip={onFlip}
        className={className}
        style={style}
        ariaLabel="SAS EuroBonus Mastercard"
        front={<SASMastercardFront width={width} last4={lastFour(cardNumber)} logoSrc={logoSrc} />}
        back={
          <CardBack
            width={width}
            theme={THEME.mastercard}
            last4Full={cardNumber}
            holderName={holderName}
            brand={<MastercardLogo size={width * 0.13} />}
          />
        }
      />
    );
  },
);

/* ===================== VARIANT 2: SAS AMEX ELITE (SORT METALL) ============= */

const SASAmexFront = memo(function SASAmexFront({
  width,
  last4,
  holderName,
  memberSince,
  logoSrc,
}: {
  width: number;
  last4: string;
  holderName: string;
  memberSince: string;
  logoSrc?: string | undefined;
}) {
  const px = createPx(width);
  const grey = "#b9bbbd";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(130deg,#171717 0%,#1b1b1b 42%,#252525 58%,#171717 100%)",
      }}
    >
      {/* børstet metall-tekstur */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "repeating-linear-gradient(180deg,rgba(255,255,255,.022) 0 1px,transparent 1px 3px)",
          mixBlendMode: "screen",
        }}
      />
      {/* diagonal glansstripe – myk, men tydelig */}
      <div
        style={{
          position: "absolute",
          top: "-30%",
          left: "30%",
          width: "58%",
          height: "160%",
          transform: "rotate(20deg)",
          background:
            "linear-gradient(90deg,transparent 0%,rgba(255,255,255,.05) 40%,rgba(255,255,255,.11) 50%,rgba(255,255,255,.05) 60%,transparent 100%)",
          filter: "blur(3px)",
          pointerEvents: "none",
        }}
      />

      {/* vertikal AMERICAN EXPRESS langs venstre kant */}
      <div
        style={{
          position: "absolute",
          left: px(2),
          top: 0,
          bottom: 0,
          width: px(26),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            transform: "rotate(-90deg)",
            whiteSpace: "nowrap",
            color: grey,
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            fontWeight: 700,
            fontSize: px(13),
            letterSpacing: px(2),
          }}
        >
          AMERICAN EXPRESS
        </span>
      </div>

      {/* SAS + EuroBonus øverst til høyre */}
      <div style={{ position: "absolute", right: px(24), top: px(20) }}>
        <SASEuroBonusLogo height={px(48)} src={logoSrc} />
      </div>

      {/* kontaktløs + siste 4 */}
      <div
        style={{
          position: "absolute",
          right: px(24),
          top: px(100),
          display: "flex",
          alignItems: "center",
          gap: px(8),
          color: "#fff",
        }}
      >
        <Contactless size={px(20)} color="#fff" />
        <span style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: px(16) }}>
          {last4}
        </span>
      </div>

      {/* Member Since – topp på linje med toppkanten av Amex-boksen */}
      <div
        style={{
          position: "absolute",
          left: px(212),
          top: px(170),
          display: "inline-block",
          color: "#e6e6e6",
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <div style={{ fontSize: px(9), fontWeight: 600, lineHeight: 1, whiteSpace: "nowrap" }}>
          Member Since
        </div>
        <div style={{ fontSize: px(14), lineHeight: 1, marginTop: px(4), textAlign: "right" }}>
          {memberSince}
        </div>
      </div>

      {/* navn – krymper automatisk hvis det blir langt */}
      <div style={{ position: "absolute", left: px(34), bottom: px(18), lineHeight: 1 }}>
        <AutoFitText
          text={holderName}
          maxWidth={px(168)}
          fontSize={px(16)}
          minFontSize={px(10)}
          style={{
            color: "#fff",
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            fontWeight: 500,
            letterSpacing: "0.06em",
            lineHeight: 1,
          }}
        />
      </div>

      {/* © AMEX – bunn på linje med bunnkanten av Amex-boksen */}
      <div
        style={{
          position: "absolute",
          left: px(212),
          bottom: px(20),
          lineHeight: 1,
          color: "#8a8a8a",
          fontSize: px(8),
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        © AMEX
      </div>

      {/* Amex-boks nede til høyre */}
      <div style={{ position: "absolute", right: px(24), bottom: px(20) }}>
        <AmexBoxLogo size={px(50)} color="#fff" />
        <span
          style={{
            position: "absolute",
            left: -px(12),
            top: px(2),
            color: "#8a8a8a",
            fontSize: px(9),
          }}
        >
          ®
        </span>
      </div>
    </div>
  );
});

/** SAS EuroBonus American Express Elite (sort metall). */
export const SASAmexElite = forwardRef<WalletCardHandle, AmexCardProps>(function SASAmexElite(
  {
    cardNumber = "3759 8765 4321 7997",
    holderName = "C F FROST",
    memberSince = "09",
    width = 360,
    flipAxis = "horizontal",
    flipped,
    defaultFlipped,
    onFlip,
    logoSrc,
    className,
    style,
  },
  ref,
) {
  return (
    <WalletCard
      ref={ref}
      width={width}
      flipAxis={flipAxis}
      flipped={flipped}
      defaultFlipped={defaultFlipped}
      onFlip={onFlip}
      className={className}
      style={style}
      ariaLabel="SAS EuroBonus American Express Elite"
      front={
        <SASAmexFront
          width={width}
          last4={lastFour(cardNumber)}
          holderName={holderName}
          memberSince={memberSince}
          logoSrc={logoSrc}
        />
      }
      back={
        <CardBack
          width={width}
          theme={THEME.amex}
          last4Full={cardNumber}
          holderName={holderName}
          brand={<AmexBoxLogo size={width * 0.12} color="#fff" />}
        />
      }
    />
  );
});
